#title-case Editor de Publicidades
<template lang="pug">
v-container(fluid)
  v-card
    v-card-title
      | {{ isNew ? 'Nueva Publicidad' : 'Editar Publicidad' }}
      v-spacer
      v-btn(
        color="primary"
        @click="goBack"
        text
      )
        v-icon(left) mdi-arrow-left
        | Volver
    
    v-card-text
      v-form(ref="form" v-model="valid" lazy-validation)
        v-row
          v-col(cols="12" md="6")
            v-text-field(
              v-model="form.title"
              label="Título*"
              :rules="rules.required"
              outlined
              dense
              counter="60"
              maxlength="60"
            )
          
          v-col(cols="12" md="6")
            v-text-field(
              v-model="form.buttonText"
              label="Texto del botón"
              outlined
              dense
              counter="15"
              maxlength="15"
              hint="Ejemplo: 'Ver más', 'Comprar'"
            )
            
          v-col(cols="12")
            v-textarea(
              v-model="form.description"
              label="Descripción*"
              :rules="rules.required"
              outlined
              dense
              counter="120"
              maxlength="120"
              rows="3"
              auto-grow
            )

          v-col(cols="12" md="8")
            v-text-field(
              v-model="form.targetUrl"
              label="URL de destino*"
              :rules="rules.url"
              outlined
              dense
              hint="URL completo donde se redirigirá al hacer clic"
              persistent-hint
            )
            
          v-col(cols="12" md="4")
            v-slider(
              v-model="form.priority"
              label="Prioridad"
              hint="Mayor prioridad = se muestra primero"
              persistent-hint
              min="0"
              max="10"
              thumb-label
              ticks
            )
            
          v-col(cols="12" md="6")
            v-menu(
              ref="startDateMenu"
              v-model="startDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            )
              template(v-slot:activator="{ on, attrs }")
                v-text-field(
                  :value="formatDate(form.startDate)"
                  label="Fecha de inicio*"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  :rules="rules.required"
                  outlined
                  dense
                  prepend-icon="mdi-calendar"
                )
              v-date-picker(
                v-model="form.startDate"
                @input="startDateMenu = false"
                :max="form.endDate"
                first-day-of-week="1"
              )
              
          v-col(cols="12" md="6")
            v-menu(
              ref="endDateMenu"
              v-model="endDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            )
              template(v-slot:activator="{ on, attrs }")
                v-text-field(
                  :value="formatDate(form.endDate)"
                  label="Fecha de fin*"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  :rules="rules.required"
                  outlined
                  dense
                  prepend-icon="mdi-calendar"
                )
              v-date-picker(
                v-model="form.endDate"
                @input="endDateMenu = false"
                :min="form.startDate"
                first-day-of-week="1"
              )
              
          v-col(cols="12")
            v-switch(
              v-model="form.active"
              label="Activo"
              color="success"
              inset
              hint="Desactiva temporalmente la publicidad sin eliminarla"
              persistent-hint
            )
            
          v-col(cols="12")
            h3.subtitle-1.mb-3 Programación por días
            p.text-caption.mb-4 Configura los días y horarios específicos en que se mostrará la publicidad. Si no configuras ningún día, se mostrará durante todo el período.
            
            v-expansion-panels(flat)
              v-expansion-panel(v-for="(day, index) in weekDays" :key="index")
                v-expansion-panel-header
                  v-row.align-center
                    v-col(cols="6")
                      strong {{ day.name }}
                    v-col(cols="6" class="text-right")
                      v-switch(
                        v-model="getSchedule(index).active"
                        color="primary"
                        hide-details
                        dense
                        class="mt-0 pt-0"
                        @change="updateSchedule(index)"
                      )
                v-expansion-panel-content
                  v-row
                    v-col(cols="12" sm="6")
                      v-menu(
                        ref="`startTimeMenu${index}`"
                        v-model="startTimeMenus[index]"
                        :close-on-content-click="false"
                        :nudge-right="40"
                        transition="scale-transition"
                        offset-y
                        max-width="290px"
                        min-width="290px"
                      )
                        template(v-slot:activator="{ on, attrs }")
                          v-text-field(
                            v-model="getSchedule(index).startTime"
                            label="Hora inicio"
                            prepend-icon="mdi-clock-time-four-outline"
                            readonly
                            v-bind="attrs"
                            v-on="on"
                            outlined
                            dense
                            :disabled="!getSchedule(index).active"
                          )
                        v-time-picker(
                          v-if="startTimeMenus[index]"
                          v-model="getSchedule(index).startTime"
                          full-width
                          format="24hr"
                          @click:minute="startTimeMenus[index] = false"
                        )
                    v-col(cols="12" sm="6")
                      v-menu(
                        ref="`endTimeMenu${index}`"
                        v-model="endTimeMenus[index]"
                        :close-on-content-click="false"
                        :nudge-right="40"
                        transition="scale-transition"
                        offset-y
                        max-width="290px"
                        min-width="290px"
                      )
                        template(v-slot:activator="{ on, attrs }")
                          v-text-field(
                            v-model="getSchedule(index).endTime"
                            label="Hora fin"
                            prepend-icon="mdi-clock-time-four-outline"
                            readonly
                            v-bind="attrs"
                            v-on="on"
                            outlined
                            dense
                            :disabled="!getSchedule(index).active"
                          )
                        v-time-picker(
                          v-if="endTimeMenus[index]"
                          v-model="getSchedule(index).endTime"
                          full-width
                          format="24hr"
                          @click:minute="endTimeMenus[index] = false"
                        )
          
          v-col(cols="12")
            h3.subtitle-1.mb-3 Imagen de la publicidad*
            p.text-caption.mb-4 La imagen debe tener una proporción de aproximadamente 16:9 para verse correctamente en el carrusel.
            
            v-card(outlined class="pa-3")
              v-row
                v-col(cols="12" sm="6" md="4")
                  div(
                    v-if="!imagePreview && !form.image"
                    class="image-upload-placeholder d-flex align-center justify-center"
                    @click="$refs.imageInput.click()"
                  )
                    v-icon(size="48" color="grey lighten-1") mdi-image-plus
                    input(
                      ref="imageInput"
                      type="file"
                      accept="image/*"
                      class="d-none"
                      @change="onImageSelected"
                    )
                  v-img(
                    v-else
                    :src="imagePreview || form.image.url"
                    aspect-ratio="16/9"
                    class="grey lighten-2"
                    width="100%"
                  )
                    template(v-slot:placeholder)
                      v-row(
                        class="fill-height ma-0"
                        align="center"
                        justify="center"
                      )
                        v-progress-circular(indeterminate color="primary")
                v-col
                  v-btn(
                    color="primary"
                    @click="$refs.imageInput.click()"
                    :disabled="loading"
                    class="mb-2"
                  )
                    v-icon(left) mdi-upload
                    | {{ imagePreview || form.image ? 'Cambiar imagen' : 'Subir imagen' }}
                  
                  input(
                    ref="imageInput"
                    type="file"
                    accept="image/*"
                    class="d-none"
                    @change="onImageSelected"
                  )
                  
                  v-btn(
                    v-if="imagePreview || form.image"
                    color="error"
                    text
                    @click="clearImage"
                    :disabled="loading"
                  )
                    v-icon(left) mdi-delete
                    | Eliminar imagen

        v-divider.my-5
        
        v-row
          v-col(cols="12" class="d-flex justify-end")
            v-btn(
              color="error"
              outlined
              class="mr-2"
              @click="goBack"
              :disabled="loading"
            ) Cancelar
            v-btn(
              color="success"
              @click="saveAdvertisement"
              :loading="loading"
              :disabled="!valid || loading || (!isNew && !imagePreview && !form.image)"
            )
              v-icon(left) mdi-content-save
              | Guardar
</template>

<script>
import { format } from 'date-fns'
import { es } from 'date-fns/locale'

export default {
  name: 'AdvertisementForm',
  
  data: () => ({
    valid: false,
    loading: false,
    imageUploading: false,
    form: {
      title: '',
      description: '',
      buttonText: 'Ver más',
      targetUrl: '',
      active: true,
      priority: 5,
      startDate: new Date().toISOString().substr(0, 10),
      endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().substr(0, 10),
      schedule: []
    },
    imagePreview: null,
    imageFile: null,
    startDateMenu: false,
    endDateMenu: false,
    startTimeMenus: Array(7).fill(false),
    endTimeMenus: Array(7).fill(false),
    weekDays: [
      { name: 'Domingo', value: 0 },
      { name: 'Lunes', value: 1 },
      { name: 'Martes', value: 2 },
      { name: 'Miércoles', value: 3 },
      { name: 'Jueves', value: 4 },
      { name: 'Viernes', value: 5 },
      { name: 'Sábado', value: 6 }
    ],
    rules: {
      required: [v => !!v || 'Este campo es obligatorio'],
      url: [
        v => !!v || 'La URL es obligatoria',
        v => {
          try {
            // Intentar crear un objeto URL válido
            new URL(v.startsWith('http') ? v : `http://${v}`);
            return true;
          } catch (e) {
            return 'Introduce una URL válida';
          }
        }
      ]
    }
  }),
  
  computed: {
    isNew() {
      return this.$route.params.id === 'new'
    },
    
    minDate() {
      return new Date().toISOString().substr(0, 10)
    }
  },
  
  methods: {
    formatDate(date) {
      if (!date) return ''
      
      // Utilizar el método parseISO para manejar correctamente las fechas ISO
      // y evitar problemas con zonas horarias
      try {
        const dateObj = new Date(date + 'T12:00:00')
        return format(dateObj, 'dd/MM/yyyy', { locale: es })
      } catch (error) {
        console.error('Error al formatear fecha:', error)
        return date
      }
    },
    
    getSchedule(day) {
      // Buscar si ya existe una programación para este día
      const existingSchedule = this.form.schedule.find(s => s.day === day)
      
      // Si existe, devolverla
      if (existingSchedule) {
        return existingSchedule
      }
      
      // Si no existe, crear una nueva y agregarla
      const newSchedule = {
        day,
        active: false,
        startTime: '00:00',
        endTime: '23:59'
      }
      
      this.form.schedule.push(newSchedule)
      return newSchedule
    },
    
    updateSchedule(day) {
      // Actualizar para mantener la reactividad
      this.$forceUpdate()
    },
    
    async onImageSelected(event) {
      const file = event.target.files[0]
      if (!file) return
      
      // Validar tamaño (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        this.$toast.error('La imagen no puede superar los 5MB')
        return
      }
      
      // Guardar archivo para subir posteriormente
      this.imageFile = file
      
      // Mostrar previsualización
      const reader = new FileReader()
      reader.onload = e => {
        this.imagePreview = e.target.result
      }
      reader.readAsDataURL(file)
      
      // Subir la imagen directamente al servidor cuando se selecciona
      await this.uploadImage()
    },
    
    async uploadImage() {
      if (!this.imageFile) return
      
      try {
        this.imageUploading = true
        
        // Crear FormData solo para la imagen
        const formData = new FormData()
        formData.append('image', this.imageFile)
        
        // Subir imagen mediante la acción del store
        const response = await this.$store.dispatch('advertisements/uploadImage', formData)
        
        // Actualizar la URL de la imagen en el formulario
        this.form.image = {
          url: response.url,
          key: response.key
        }
        
        // Ya no necesitamos almacenar el archivo, ya que se ha subido
        this.imageFile = null
        
        this.$toast.success('Imagen subida correctamente')
      } catch (error) {
        this.$toast.error('Error al subir la imagen')
        console.error('Error al subir imagen:', error)
      } finally {
        this.imageUploading = false
      }
    },
    
    clearImage() {
      this.imagePreview = null
      this.imageFile = null
      this.form.image = null
      this.$refs.imageInput.value = ''
    },
    
    async loadAdvertisement() {
      if (this.isNew) return
      
      this.loading = true
      try {
        await this.$store.dispatch('advertisements/getAdvertisement', this.$route.params.id)
        const ad = this.$store.state.advertisements.currentAdvertisement
        
        if (ad) {
          // Copiar datos al formulario
          this.form = {
            title: ad.title,
            description: ad.description,
            buttonText: ad.buttonText || 'Ver más',
            targetUrl: ad.targetUrl,
            active: ad.active,
            priority: ad.priority || 0,
            startDate: new Date(ad.startDate).toISOString().substr(0, 10),
            endDate: new Date(ad.endDate).toISOString().substr(0, 10),
            image: ad.image,
            schedule: ad.schedule || []
          }
        }
      } catch (error) {
        this.$toast.error('Error al cargar la publicidad')
        console.error('Error cargando publicidad:', error)
      } finally {
        this.loading = false
      }
    },
    
    async saveAdvertisement() {
      if (!this.$refs.form.validate()) return
      
      if (!this.form.image) {
        this.$toast.error('La imagen es obligatoria')
        return
      }
      
      // Crear FormData para envío
      const formData = new FormData()
      formData.append('title', this.form.title)
      formData.append('description', this.form.description)
      formData.append('buttonText', this.form.buttonText)
      formData.append('targetUrl', this.form.targetUrl)
      formData.append('active', this.form.active)
      formData.append('priority', this.form.priority)
      formData.append('startDate', this.form.startDate)
      formData.append('endDate', this.form.endDate)
      formData.append('schedule', JSON.stringify(this.form.schedule))
      
      // Añadir la información de la imagen que ya ha sido subida
      if (this.form.image) {
        formData.append('image', JSON.stringify(this.form.image))
      }
      
      this.loading = true
      try {
        if (this.isNew) {
          await this.$store.dispatch('advertisements/createAdvertisement', formData)
          this.$toast.success('Publicidad creada correctamente')
        } else {
          await this.$store.dispatch('advertisements/updateAdvertisement', {
            id: this.$route.params.id,
            formData
          })
          this.$toast.success('Publicidad actualizada correctamente')
        }
        
        this.goBack()
      } catch (error) {
        this.$toast.error('Error al guardar la publicidad')
        console.error('Error guardando publicidad:', error)
      } finally {
        this.loading = false
      }
    },
    
    goBack() {
      this.$router.push('/manager/advertisements')
    }
  },
  
  created() {
    this.loadAdvertisement()
  }
}
</script>

<style scoped>
.image-upload-placeholder {
  width: 100%;
  height: 0;
  padding-bottom: 56.25%; /* 16:9 */
  background-color: #f5f5f5;
  border: 2px dashed #e0e0e0;
  border-radius: 4px;
  cursor: pointer;
  position: relative;
}

.image-upload-placeholder .v-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}
</style>
